import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useDispatch, useSelector } from '../../redux/hooks';
import { reorderForColumnFirst } from '../../utils';
import { updateSearch } from '../../redux/slices/globalSearch';
import { Search } from '@mesa-labs/mesa-ui';
const NavContainer = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
`;

const NavDropdownContainer = styled(motion.div) <{ zIndex?: number }>`
  position: relative;
`;

const NavDropdownLabel = styled.div<{ active: boolean, initiallyHidden: boolean }>`
  background: ${(props) => (props.active && '#FAFAFA') || 'transparent'};
  color: ${(props) => (props.active && '#252B37') || '#414651'};
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  padding: 10px 10px;
  position: relative;
  transition: background 200ms ease-in-out;

  &:hover {
    ${(props) => props.initiallyHidden && `
      background: #FAFAFA;
      color: #252B37;
    `}
  }
`;

const NavDropdownLabelBridge = styled.div`
  height: 10px;
  position: absolute;
  bottom: -10px;
  left: 0px;
  width: 100%;
  pointer-events: auto;
  z-index: 1;
`;

const NavDropdownLinks = styled(motion.div) <{ initiallyHidden: boolean, width?: number }>`
  background: ${(props) => props.theme.colors.White};
  border-radius: 8px;
  border: 1px solid #D0D5DD;
  box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.1);
  column-gap: 20px;
  display: none;
  flex-direction: column;
  grid-template-columns: auto auto;
  font-size: 13px;
  overflow: hidden;
  padding: 8px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: max-content;
  z-index: 2;

  ${(props) => props.initiallyHidden && `
    left: 0px;
    position: absolute;
    top: 48px;
    margin-top: -10px;
    padding-top: 18px;
  `}
`;

const NavDropdownLink = styled(Link)`
  border-radius: 8px;
  color: #414651;
  cursor: pointer;
  padding: 12px;
  transition: background 100ms ease-in-out;
  white-space: nowrap;

  &:hover {
    background: #FAFAFA;
    color: #252B37;
  }
`;

const ROUTES = {
  coreBasicEntities: [
    {
      label: 'Vendors',
      route: '/vendors',
    },
    {
      label: 'Programs',
      route: '/programs',
    },
    {
      label: 'Invoices',
      route: '/invoices',
    },
    {
      label: 'Invoice Line Items',
      route: '/invoice-line-items',
    },
    {
      label: 'Transfers',
      route: '/customer-batch-transfers',
    },
  ],
  coreBusinessInternals: [
    {
      label: 'Vendor Documents',
      route: '/vendor-documents',
    },
    {
      label: 'Invoice Approvals',
      route: '/invoice-approvals',
    },
    {
      label: 'Invoice Adjustment Approvals',
      route: '/invoice-adjustment-approvals',
    },
    {
      label: 'Invoice Quarantines',
      route: '/invoice-quarantines',
    },
    {
      label: 'Clients',
      route: '/clients',
    },
    {
      label: 'Enablement V2',
      route: '/enablement-v2',
    },
    {
      label: 'Onboardings',
      route: '/onboardings',
    },
    {
      label: 'Abandoned Users',
      route: '/abandoned-users',
    },
    {
      label: 'Mesa Service Agreements',
      route: '/service-agreements',
    },
    {
      label: 'Partner Service Agreements',
      route: '/partner-service-agreements',
    },
    {
      label: 'Collections',
      route: '/collections',
    },
    {
      label: 'Factoring Collections',
      route: '/factoring-collections',
    },
    {
      label: 'Reconciliation',
      route: '/reconciliation',
    },
    {
      label: 'Batch Sweeps',
      route: '/batch-sweeps',
    },
    {
      label: 'Adjustments',
      route: '/adjustments',
    },
    {
      label: 'On-Demand/In-Flights',
      route: '/ondemand-inflights',
    },
    {
      label: 'Tasks',
      route: '/tasks',
    },
    {
      label: 'Reports',
      route: '/reports',
    },
    {
      label: 'Facilitators',
      route: '/facilitators',
    },
    {
      label: 'Program Promotions',
      route: '/program-promotions',
    },
    {
      label: 'Email Sequences',
      route: '/email-sequences',
    },
    {
      label: 'Email Templates',
      route: '/email-templates',
    },
    {
      label: 'FAQ Configuration',
      route: '/faq-configuration',
    },
    {
      label: 'Factoring Access',
      route: '/factoring-access',
    },
    {
      label: 'Vendor Surveys',
      route: '/vendor-surveys',
    },
    {
      label: 'Scheduled Vendor Collections',
      route: '/scheduled-vendor-collections',
    },
    {
      label: 'Underwriting',
      route: '/underwriting',
    },
    {
      label: 'UCCs',
      route: '/uccs',
    },
  ],
  miscellaneous: [
    {
      label: 'Action Items',
      route: '/action-items',
    },
    {
      label: 'Partners',
      route: '/partners',
    },
    {
      label: 'Partner Integrations',
      route: '/partner-integrations',
    },
    {
      label: 'Partner Rev Shares',
      route: '/partner-rev-shares',
    },
    {
      label: 'Vendor Sub-Accounts',
      route: '/vendor-sub-accounts',
    },
    {
      label: 'Permissions',
      route: '/permissions',
    },
    {
      label: 'KPIs',
      route: '/key-performance-indicators',
    },
    {
      label: 'Revenue',
      route: '/revenue',
    },
    {
      label: 'Cohorts',
      route: '/cohorts',
    },
    {
      label: 'Accelerated Payout Statistics',
      route: '/accelerated-payout-statistics',
    },
    {
      label: 'Cash Flow Projections',
      route: '/projections',
    },
    {
      label: 'Configuration Settings',
      route: '/configuration-settings',
    },
    {
      label: 'AI Assistant',
      route: '/ai-assistant',
    },
    {
      label: 'Generate MSA Preview',
      route: '/generate-msa-preview',
    },
    {
      label: 'Yield Monitoring',
      route: '/yield-monitoring',
    },
    {
      label: 'Transaction Matching',
      route: '/transaction-matching',
    },
    {
      label: 'Portfolio Monitoring',
      route: '/portfolio-monitoring',
    },
  ],
  internalData: [
    {
      label: 'Internal Vendors',
      route: '/internal-vendors',
    },
    {
      label: 'Internal Clients',
      route: '/internal-clients',
    },
    {
      label: 'Internal Client Vendors',
      route: '/internal-client-vendors',
    },
  ],
  externalData: [
    {
      label: 'External Vendors',
      route: '/external-vendors',
    },
    {
      label: 'External Clients',
      route: '/external-clients',
    },
    {
      label: 'External Client Vendors',
      route: '/external-client-vendors',
    },
    {
      label: 'External Vendor Statistics',
      route: '/external-vendor-statistics',
    },
    {
      label: 'External Invoices',
      route: '/external-invoices',
    },
    {
      label: 'Data Reconciliation',
      route: '/data-reconciliation',
    },
  ],
};

type PageMenuProps = {
  initial: 'hidden' | 'hover',
};

function PageMenu({ initial }: PageMenuProps): React.ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);

  const canViewBasicEntities = useSelector((state) => state.auth.canViewBasicEntities);
  const canViewBusinessInternals = useSelector((state) => state.auth.canViewBusinessInternals);
  const search = useSelector((state) => state.globalSearch.search);

  const canViewBasicNav = useMemo(() => isLoggedIn, [isLoggedIn]);

  const [searchInput, setSearchInput] = useState(search);

  const isActiveLabel = (routes: { label: string, route: string }[]) => {
    if (initial === 'hover') {
      return false;
    }

    return routes.some((route) => route.route === location.pathname || location.pathname.startsWith(`${route.route}/`));
  }

  const corePlatformRoutes = useMemo(() => {
    return [
      ...(canViewBasicEntities && ROUTES.coreBasicEntities || []),
      ...(canViewBusinessInternals && ROUTES.coreBusinessInternals || []),
    ].sort((a, b) => a.label.localeCompare(b.label));
  }, [canViewBasicEntities, canViewBusinessInternals]);

  const miscellaneousRoutes = useMemo(() => ROUTES.miscellaneous.sort((a, b) => a.label.localeCompare(b.label)), []);
  const internalDataRoutes = useMemo(() => ROUTES.internalData.sort((a, b) => a.label.localeCompare(b.label)), []);
  const externalDataRoutes = useMemo(() => ROUTES.externalData.sort((a, b) => a.label.localeCompare(b.label)), []);

  if (!isLoggedIn) {
    return <></>;
  }

  return (
    <NavContainer>
      <AnimatePresence>
        {canViewBasicNav && (
          <NavDropdownContainer initial={initial} whileHover="hover" exit={initial}>
            <NavDropdownLabel
              initiallyHidden={initial === 'hidden'}
              active={isActiveLabel([...ROUTES.coreBasicEntities, ...ROUTES.coreBusinessInternals])}
            >
              Core
              <NavDropdownLabelBridge />
            </NavDropdownLabel>
            <NavDropdownLinks
              key="basic-links"
              initiallyHidden={initial === 'hidden'}
              variants={{
                hidden: {
                  opacity: 0,
                  pointerEvents: 'none',
                  visibility: 'hidden',
                },
                hover: {
                  display: 'grid',
                  opacity: 1,
                  pointerEvents: 'auto',
                  visibility: 'visible',
                },
              }}
              transition={{
                duration: 0.2,
              }}
            >
              {reorderForColumnFirst(corePlatformRoutes).map((route) => <NavDropdownLink key={route.route} to={route.route}>{route.label}</NavDropdownLink>)}
            </NavDropdownLinks>
          </NavDropdownContainer>
        )}
      </AnimatePresence>
      <AnimatePresence>
        <NavDropdownContainer initial={initial} whileHover="hover" exit={initial}>
          <NavDropdownLabel
            initiallyHidden={initial === 'hidden'}
            active={isActiveLabel(ROUTES.miscellaneous)}
          >
            Miscellaneous
            <NavDropdownLabelBridge />
          </NavDropdownLabel>
          <NavDropdownLinks
            key="miscellaneous-links"
            initiallyHidden={initial === 'hidden'}
            variants={{
              hidden: {
                display: 'none',
                opacity: 0,
              },
              hover: {
                display: 'grid',
                opacity: 1,
              },
            }}
            transition={{
              type: 'spring',
              duration: 0.2,
            }}
          >
            {reorderForColumnFirst(miscellaneousRoutes).map((route) => <NavDropdownLink key={route.route} to={route.route}>{route.label}</NavDropdownLink>)}
          </NavDropdownLinks>
        </NavDropdownContainer>
      </AnimatePresence>
      <AnimatePresence>
        <NavDropdownContainer initial={initial} whileHover="hover" exit={initial}>
          <NavDropdownLabel
            initiallyHidden={initial === 'hidden'}
            active={isActiveLabel(ROUTES.internalData)}
          >
            Internal Data
            <NavDropdownLabelBridge />
          </NavDropdownLabel>
          <NavDropdownLinks
            key="internal-data-links"
            initiallyHidden={initial === 'hidden'}
            variants={{
              hidden: {
                display: 'none',
                opacity: 0,
              },
              hover: {
                display: 'flex',
                opacity: 1,
              },
            }}
            transition={{
              duration: 0.2,
            }}
          >
            {reorderForColumnFirst(internalDataRoutes).map((route) => <NavDropdownLink key={route.route} to={route.route}>{route.label}</NavDropdownLink>)}
          </NavDropdownLinks>
        </NavDropdownContainer>
      </AnimatePresence>
      <AnimatePresence>
        <NavDropdownContainer initial={initial} whileHover="hover" exit={initial}>
          <NavDropdownLabel
            initiallyHidden={initial === 'hidden'}
            active={isActiveLabel(ROUTES.externalData)}
          >
            External Data
            <NavDropdownLabelBridge />
          </NavDropdownLabel>
          <NavDropdownLinks
            key="external-data-links"
            initiallyHidden={initial === 'hidden'}
            variants={{
              hidden: {
                display: 'none',
                opacity: 0,
              },
              hover: {
                display: 'flex',
                opacity: 1,
              },
            }}
            transition={{
              duration: 0.2,
            }}
          >
            {reorderForColumnFirst(externalDataRoutes).map((route) => <NavDropdownLink key={route.route} to={route.route}>{route.label}</NavDropdownLink>)}
          </NavDropdownLinks>
        </NavDropdownContainer>
      </AnimatePresence>
      <AnimatePresence>
        <Search
          placeholder="Search anything... "
          value={searchInput || ''}
          onChange={(value) => setSearchInput(value)}
          onSubmit={(value) => {
            dispatch(updateSearch(value));
            navigate(`/global-search?search=${value}`);
          }}
          expanded
          width="250px"
        />
      </AnimatePresence>
    </NavContainer>
  );
}

export default PageMenu;